/**
 * The only module that talks to the backend. Features call these functions (usually from
 * react-query hooks in their own api.ts) with paths relative to /api, exactly as the Ninja
 * routers declare them, trailing slashes included:
 *
 *   apiGet<Paged<JobOut>>("/jobs/", { params: { status: "running", limit: 20 } })
 *   apiPatch<WorkspaceOut>("/workspace/settings", { settings: { theme: "dark" } })
 *   apiUpload<DocumentOut>("/documents/", { file, kind: "sds" })
 *
 * Session cookies ride along (same origin). Unsafe methods send X-CSRFToken from the `csrftoken`
 * cookie, re-read on every request because Django rotates it at login. Failures throw ApiError.
 */

export const API_PREFIX = "/api";

const CSRF_COOKIE = "csrftoken";
const CSRF_HEADER = "X-CSRFToken";
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export type QueryParamValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryParamValue | ReadonlyArray<string | number | boolean>>;

export interface RequestOptions {
  params?: QueryParams;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/** Error body shape of the backend: `{"detail": ..., "code": ...}` (see backend/common/errors.py). */
export interface ApiErrorBody {
  detail?: unknown;
  code?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  /** HTTP status; 0 when the server could not be reached. */
  readonly status: number;
  /** A sentence the UI can show as is. */
  readonly detail: string;
  /** The backend's stable error code ("not_found", "read_only", "job_kind_not_allowed"…), if any. */
  readonly code: string | undefined;
  /** Field-level messages keyed by dotted field path ("ship_to.country"), for form errors. */
  readonly fieldErrors: Readonly<Record<string, string>>;
  readonly body: unknown;

  constructor(
    status: number,
    detail: string,
    options: { code?: string; fieldErrors?: Record<string, string>; body?: unknown } = {},
  ) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.code = options.code;
    this.fieldErrors = options.fieldErrors ?? {};
    this.body = options.body;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export function isApiError(error: unknown, status?: number): error is ApiError {
  return error instanceof ApiError && (status === undefined || error.status === status);
}

/** A sentence for any thrown value, for error states and toasts. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Try again.";
}

// 401 handling ------------------------------------------------------------------------------------

type UnauthorizedHandler = (error: ApiError) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Called when a request outside /auth/ comes back 401 (the session expired). The app registers a
 * handler that clears the cached session and sends the user to /login.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

// CSRF --------------------------------------------------------------------------------------------

/** The current `csrftoken` cookie value, read fresh from document.cookie. */
export function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  for (const cookie of document.cookie.split(";")) {
    const [name, ...rest] = cookie.trim().split("=");
    if (name === CSRF_COOKIE) return decodeURIComponent(rest.join("="));
  }
  return null;
}

let csrfRequest: Promise<string> | null = null;

/** Makes sure a CSRF token exists, asking GET /api/auth/csrf to set the cookie when needed. */
export async function ensureCsrfToken({ refresh = false } = {}): Promise<string> {
  const existing = readCsrfToken();
  if (existing && !refresh) return existing;
  csrfRequest ??= (async () => {
    try {
      const body = await request<{ csrf_token: string }>("GET", "/auth/csrf");
      return readCsrfToken() ?? body.csrf_token;
    } finally {
      csrfRequest = null;
    }
  })();
  return csrfRequest;
}

// URLs --------------------------------------------------------------------------------------------

/** Absolute-path URL for an API path, e.g. for <a href> downloads: apiUrl("/documents/1/download"). */
export function apiUrl(path: string, params?: QueryParams): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item === null || item === undefined || item === "") continue;
      search.append(key, String(item));
    }
  }
  const query = search.toString();
  return `${API_PREFIX}${normalizedPath}${query ? `?${query}` : ""}`;
}

// Error mapping -----------------------------------------------------------------------------------

const LOCATION_PREFIXES = new Set(["body", "query", "path", "form", "header", "cookie", "file"]);
const PARAMETER_NAMES = new Set(["payload", "body", "data", "params", "filters"]);

function fieldPath(location: unknown): string {
  if (!Array.isArray(location)) return "";
  const parts = location.map(String);
  if (parts.length > 1 && LOCATION_PREFIXES.has(parts[0] ?? "")) parts.shift();
  if (parts.length > 1 && PARAMETER_NAMES.has(parts[0] ?? "")) parts.shift();
  return parts.join(".");
}

function messageFor(status: number): string {
  if (status === 400) return "The request was not accepted. Check the values and try again.";
  if (status === 401) return "Your session has ended. Sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "Not found. It may have been deleted or moved.";
  if (status === 409) return "This change conflicts with existing data.";
  if (status === 413) return "The file is too large to upload.";
  if (status === 422) return "Some fields need attention.";
  if (status >= 500 && status !== 501) {
    return status === 500
      ? "The server hit an error. Try again; if it keeps happening, check the API logs."
      : "The server isn't responding. Check that the API is running, then try again.";
  }
  if (status === 501) return "This isn't available yet.";
  return `Request failed (HTTP ${status}).`;
}

/** Builds an ApiError from a response body in any of the backend's shapes. */
export function toApiError(status: number, body: unknown): ApiError {
  const errorBody: ApiErrorBody = body && typeof body === "object" ? (body as ApiErrorBody) : {};
  const code = typeof errorBody.code === "string" ? errorBody.code : undefined;
  const { detail } = errorBody;
  const fieldErrors: Record<string, string> = {};

  if (typeof detail === "string" && detail.trim()) {
    return new ApiError(status, detail, { code, body });
  }
  if (Array.isArray(detail)) {
    // Ninja/pydantic validation: [{ loc: ["body", "payload", "ship_to", "country"], msg: "..." }]
    for (const item of detail as Array<{ loc?: unknown; msg?: unknown }>) {
      const path = fieldPath(item?.loc) || "_";
      const message = typeof item?.msg === "string" ? item.msg : JSON.stringify(item);
      fieldErrors[path] = fieldErrors[path] ? `${fieldErrors[path]} ${message}` : message;
    }
  } else if (detail && typeof detail === "object") {
    // Django ValidationError.message_dict: { field: ["message", ...] }
    for (const [field, messages] of Object.entries(detail as Record<string, unknown>)) {
      fieldErrors[field] = Array.isArray(messages) ? messages.map(String).join(" ") : String(messages);
    }
  }
  const messages = Object.entries(fieldErrors);
  if (messages.length === 1) {
    const [[field, message]] = messages as [[string, string]];
    const summary = field === "_" || field === "__all__" ? message : `${field.replaceAll("_", " ")}: ${message}`;
    return new ApiError(status, summary, { code, fieldErrors, body });
  }
  return new ApiError(status, messages.length > 1 ? "Some fields need attention." : messageFor(status), {
    code,
    fieldErrors,
    body,
  });
}

// Requests ----------------------------------------------------------------------------------------

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

function isCsrfFailure(status: number, body: unknown): boolean {
  if (status !== 403) return false;
  const detail = body && typeof body === "object" ? (body as ApiErrorBody).detail : body;
  return typeof detail === "string" && /csrf/i.test(detail);
}

interface InternalRequest extends RequestOptions {
  body?: BodyInit;
  contentType?: string;
  /** Return the raw Response (downloads) instead of the parsed body. */
  raw?: boolean;
}

async function send(method: string, path: string, init: InternalRequest, csrfToken: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json", ...init.headers };
  if (init.contentType) headers["Content-Type"] = init.contentType;
  if (csrfToken) headers[CSRF_HEADER] = csrfToken;
  try {
    return await fetch(apiUrl(path, init.params), {
      method,
      headers,
      body: init.body,
      credentials: "same-origin",
      signal: init.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(0, "Can't reach the server. Check your connection and that the app is running.", {
      code: "network_error",
    });
  }
}

async function request<T>(method: string, path: string, init: InternalRequest = {}): Promise<T> {
  const unsafe = UNSAFE_METHODS.has(method);
  let csrfToken = unsafe ? await ensureCsrfToken() : null;
  let response = await send(method, path, init, csrfToken);

  if (!response.ok && unsafe && response.status === 403) {
    const body = await parseBody(response.clone());
    if (isCsrfFailure(response.status, body)) {
      csrfToken = await ensureCsrfToken({ refresh: true });
      response = await send(method, path, init, csrfToken);
    }
  }

  if (!response.ok) {
    const error = toApiError(response.status, await parseBody(response));
    if (response.status === 401 && !path.startsWith("/auth/")) unauthorizedHandler?.(error);
    throw error;
  }
  if (init.raw) return response as T;
  return (await parseBody(response)) as T;
}

function jsonBody(body: unknown): Pick<InternalRequest, "body" | "contentType"> {
  return body === undefined ? {} : { body: JSON.stringify(body), contentType: "application/json" };
}

export function apiGet<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return request<T>("GET", path, options);
}

export function apiPost<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  return request<T>("POST", path, { ...options, ...jsonBody(body) });
}

export function apiPut<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  return request<T>("PUT", path, { ...options, ...jsonBody(body) });
}

export function apiPatch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  return request<T>("PATCH", path, { ...options, ...jsonBody(body) });
}

export function apiDelete<T = void>(path: string, options: RequestOptions = {}): Promise<T> {
  return request<T>("DELETE", path, options);
}

export type UploadFields = Record<string, string | Blob | null | undefined>;

/**
 * Multipart POST. Pass FormData, or an object whose Blob/File values become file parts and whose
 * strings become form fields (null/undefined are skipped). The browser sets the boundary header.
 */
export function apiUpload<T>(path: string, fields: FormData | UploadFields, options: RequestOptions = {}): Promise<T> {
  let form: FormData;
  if (fields instanceof FormData) {
    form = fields;
  } else {
    form = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      if (value === null || value === undefined) continue;
      form.append(key, value);
    }
  }
  return request<T>("POST", path, { ...options, body: form });
}

export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

/** GET a binary response (PDF labels, exports) with session auth and API error handling. */
export async function apiDownload(path: string, options: RequestOptions = {}): Promise<DownloadedFile> {
  const response = await request<Response>("GET", path, { ...options, raw: true });
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return { blob: await response.blob(), filename: match?.[1] ? decodeURIComponent(match[1]) : null };
}
